import { useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import useScrollReveal from '../hooks/useScrollReveal';
import WaveDivider from '../components/WaveDivider';
import PageHero from '../components/PageHero';

const FAQSection = styled.section`
  background: var(--cream);
  padding: 100px 24px;
`;

const FAQContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const FAQItem = styled.div`
  border-bottom: 1px solid var(--border-light);
`;

const Question = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 0;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
  gap: 16px;
  transition: color 0.3s ease;
`;

const QuestionText = styled.span`
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-size: 1.05rem;
  color: ${({ $active }) => ($active ? 'var(--spruce)' : 'var(--text-dark)')};
  transition: color 0.3s ease;

  ${Question}:hover & {
    color: var(--spruce);
  }
`;

const ToggleIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  font-size: 1.3rem;
  color: var(--sage);
  transition: transform 0.4s ease;
  transform: ${({ $active }) => ($active ? 'rotate(45deg)' : 'rotate(0deg)')};
`;

const Answer = styled.div`
  max-height: ${({ $open }) => ($open ? '500px' : '0')};
  overflow: hidden;
  transition: max-height 0.4s ease, padding 0.4s ease;
  padding: ${({ $open }) => ($open ? '0 0 20px 0' : '0')};
`;

const AnswerText = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 1rem;
  color: var(--text-body);
  line-height: 1.8;
  margin: 0;
`;

const InlineLink = styled(Link)`
  color: var(--sage-muted);
  font-weight: 600;
  transition: color 0.3s ease;

  &:hover {
    color: var(--spruce);
  }
`;

const CTASection = styled.section`
  background: var(--mist);
  padding: 100px 24px;
  text-align: center;
`;

const CTAInner = styled.div`
  max-width: 600px;
  margin: 0 auto;
`;

const CTAHeading = styled.h2`
  font-family: 'Lora', serif;
  font-size: clamp(1.8rem, 3vw, 2.4rem);
  font-weight: 600;
  color: var(--text-dark);
  margin-bottom: 16px;
`;

const CTASubtitle = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 1rem;
  color: var(--text-body);
  line-height: 1.7;
  margin-bottom: 32px;
`;

const CTAButton = styled(Link)`
  display: inline-block;
  background: var(--sage);
  color: white;
  padding: 14px 36px;
  border-radius: 4px;
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-size: 0.8rem;
  transition: background 0.3s ease;

  &:hover {
    background: var(--sage-muted);
  }
`;

const faqData = [
  {
    id: 1,
    question: 'Do you accept insurance?',
    answer:
      'I do not accept insurance. If your insurance plan has out-of-network benefits, I may be able to provide you with a superbill to submit for reimbursement. If you choose to go this route, you are still responsible for payment.',
  },
  {
    id: 2,
    question: 'What are your rates?',
    answer: 'rates',
  },
  {
    id: 3,
    question: 'Where are you licensed?',
    answer:
      'I am currently licensed in Vermont, Massachusetts, California, and Pennsylvania. I provide telehealth to people located in any one of these states, as well as in-person appointments to people at my office in Northern Vermont.',
  },
  {
    id: 4,
    question: 'Do you provide any specialized treatment?',
    answer:
      'I do! I specialize in relationship therapy (commonly called couples therapy) and family therapy. In 2025 I completed a postgraduate fellowship at the Psychodynamic Couple and Family Institute of New England. In this work, I particularly focus on work with mixed neurotype relationships, queer relationships, and expectant and new parents. I am also able to provide EMDR either as primary therapy or as adjunct therapy to support work with your primary therapist. I am able to weave EMDR into our therapy even if it is not our primary focus.',
  },
];

const FAQs = () => {
  useScrollReveal();
  const [openId, setOpenId] = useState(null);

  const toggle = (id) => {
    setOpenId(openId === id ? null : id);
  };

  const renderAnswer = (item) => {
    if (item.answer === 'rates') {
      return (
        <AnswerText>
          Please see the <InlineLink to="/fees">Fees page</InlineLink> for
          information about my rates.
        </AnswerText>
      );
    }
    return <AnswerText>{item.answer}</AnswerText>;
  };

  return (
    <>
      <PageHero
        label="FAQs"
        title="Frequently Asked Questions"
        subtitle="Answers to some common questions about therapy with me."
      />

      <WaveDivider topColor="#e4e8e1" bottomColor="#eef0eb" />

      <FAQSection>
        <FAQContainer className="fade-in">
          {faqData.map((item) => (
            <FAQItem key={item.id}>
              <Question onClick={() => toggle(item.id)}>
                <QuestionText $active={openId === item.id}>
                  {item.question}
                </QuestionText>
                <ToggleIcon $active={openId === item.id}>+</ToggleIcon>
              </Question>
              <Answer $open={openId === item.id}>
                {renderAnswer(item)}
              </Answer>
            </FAQItem>
          ))}
        </FAQContainer>
      </FAQSection>

      <WaveDivider topColor="#eef0eb" bottomColor="#d8ddd5" />

      <CTASection>
        <CTAInner className="fade-in">
          <CTAHeading>Have Another Question?</CTAHeading>
          <CTASubtitle>
            Feel free to reach out &mdash; I'm happy to help.
          </CTASubtitle>
          <CTAButton to="/contact">Get in Touch</CTAButton>
        </CTAInner>
      </CTASection>
    </>
  );
};

export default FAQs;
